/**
 * Operation Manifest Types
 *
 * Based on LogicAppsUX OperationManifest patterns for Standard SKU workflows.
 * Manifests describe operation capabilities, inputs, outputs, and settings.
 */

import type { SchemaObject } from './valueSegment';

// =============================================================================
// OPERATION INFO
// =============================================================================

/**
 * Identifies a specific operation within a connector
 */
export interface OperationInfo {
  connectorId: string;
  operationId: string;
  /** Optional path for legacy tools that don't have operationId stored */
  path?: string;
  /** Optional method for legacy tools that don't have operationId stored */
  method?: string;
}

// =============================================================================
// SETTING SCOPES
// =============================================================================

export const SettingScope = {
  Trigger: 'trigger',
  Action: 'action',
} as const;
// Allow both enum values and string literals for LogicAppsUX compatibility
export type SettingScope = (typeof SettingScope)[keyof typeof SettingScope] | 'Trigger' | 'Action';

// =============================================================================
// OPERATION OPTIONS (flags for operation behavior)
// =============================================================================

export const OperationOptions = {
  Asynchronous: 'Asynchronous',
  DisableAsyncPattern: 'DisableAsyncPattern',
  DisableAutomaticDecompression: 'DisableAutomaticDecompression',
  EnableSchemaValidation: 'EnableSchemaValidation',
  SuppressWorkflowHeaders: 'SuppressWorkflowHeaders',
  SuppressWorkflowHeadersOnResponse: 'SuppressWorkflowHeadersOnResponse',
  FailWhenLimitsReached: 'FailWhenLimitsReached',
} as const;
export type OperationOptions = (typeof OperationOptions)[keyof typeof OperationOptions];

// =============================================================================
// MANIFEST SETTINGS
// =============================================================================

/**
 * Generic manifest setting wrapper
 * If scopes is undefined, the setting applies to all scopes
 */
export interface OperationManifestSetting<T = void> {
  scopes?: SettingScope[];
  options?: T;
}

/**
 * Upload chunk configuration
 */
export interface UploadChunkMetadata {
  chunkTransferSupported?: boolean;
  acceptUploadSize?: boolean;
  minimumSize?: number;
  maximumSize?: number;
}

/**
 * Download chunk configuration
 */
export interface DownloadChunkMetadata {
  acceptDownloadSize?: boolean;
  minimumSize?: number;
  maximumSize?: number;
}

/**
 * Secure data options
 */
export interface SecureDataOptions {
  outputsMode?: OutputSecureDataMode;
}

export const OutputSecureDataMode = {
  Disabled: 'disabled',
  LinkedToInputs: 'linkedtoinputs',
} as const;
// Allow both enum values and string literals for LogicAppsUX compatibility
export type OutputSecureDataMode =
  | (typeof OutputSecureDataMode)[keyof typeof OutputSecureDataMode]
  | 'LinkedToInputs'
  | 'Disabled';

/**
 * All settings that can be defined in an operation manifest
 */
export interface OperationManifestSettings {
  chunking?: OperationManifestSetting<UploadChunkMetadata>;
  concurrency?: OperationManifestSetting<void>;
  invokerConnection?: OperationManifestSetting<void>;
  correlation?: OperationManifestSetting<void>;
  downloadChunking?: OperationManifestSetting<DownloadChunkMetadata>;
  operationOptions?: OperationManifestSetting<OperationOptions[]>;
  paging?: OperationManifestSetting<void>;
  requestOptions?: OperationManifestSetting<void>;
  retryPolicy?: OperationManifestSetting<void>;
  secureData?: OperationManifestSetting<SecureDataOptions>;
  timeout?: OperationManifestSetting<void>;
  trackedProperties?: OperationManifestSetting<void>;
  count?: OperationManifestSetting<void>;
}

// =============================================================================
// CONNECTION METADATA
// =============================================================================

export const ConnectionType = {
  Function: 'function',
  ServiceProvider: 'serviceprovider',
  ApiManagement: 'apimanagement',
  ApiConnection: 'apiconnection',
  Agent: 'agent',
  Mcp: 'mcp',
} as const;
export type ConnectionType = (typeof ConnectionType)[keyof typeof ConnectionType];

export const ConnectionReferenceKeyFormat = {
  ApiConnection: 'apiconnection',
  ApiManagement: 'apimanagement',
  Function: 'function',
  OpenApi: 'openapi',
  ServiceProvider: 'serviceprovider',
  HybridTrigger: 'hybridtrigger',
  AgentConnection: 'agentconnection',
  McpConnection: 'mcpconnection',
} as const;
export type ConnectionReferenceKeyFormat =
  (typeof ConnectionReferenceKeyFormat)[keyof typeof ConnectionReferenceKeyFormat];

export interface ConnectionMetadata {
  required: boolean;
  type?: ConnectionType;
  connectionCreationClient?: string;
  disableAutoSelection?: boolean;
}

export interface ConnectionReferenceKeyFormatMapping {
  referenceKeyFormat: ConnectionReferenceKeyFormat;
}

// =============================================================================
// RECURRENCE
// =============================================================================

export const RecurrenceType = {
  None: 'none',
  Basic: 'basic',
  Advanced: 'advanced',
} as const;
export type RecurrenceType = (typeof RecurrenceType)[keyof typeof RecurrenceType];

export interface RecurrenceSetting {
  type: RecurrenceType;
  useLegacyParameterGroup?: boolean;
}

// =============================================================================
// RUN AFTER
// =============================================================================

export const RunAfterType = {
  NotSupported: 'notsupported',
} as const;
export type RunAfterType = (typeof RunAfterType)[keyof typeof RunAfterType];

export interface ManifestRunAfterSetting {
  type: RunAfterType;
}

// =============================================================================
// OUTPUTS
// =============================================================================

export interface BuiltInOutput {
  name: string;
  title: string;
  type: string;
  required: boolean;
}

export interface OutputTokens {
  selfReference?: boolean;
  disableTokens?: boolean;
  builtIns?: BuiltInOutput[];
}

// =============================================================================
// SUBGRAPH (for control flow nodes)
// =============================================================================

export interface SubGraphDetail {
  location?: string[];
  inputs?: SchemaObject;
  inputsLocation?: string[];
  isAdditive?: boolean;
  allowOperations?: boolean;
  /** Allow any additional properties for LogicAppsUX compatibility */
  [key: string]: unknown;
}

// =============================================================================
// BADGES & DOCUMENTATION
// =============================================================================

export interface Badge {
  name: string;
  description: string;
}

export interface Documentation {
  url: string;
}

// =============================================================================
// CONNECTOR INFO
// =============================================================================

export interface ConnectorInfo {
  id: string;
  name: string;
  displayName?: string;
  iconUri?: string;
  brandColor?: string;
  description?: string;
  properties?: {
    displayName?: string;
    description?: string;
    [key: string]: unknown;
  };
  /** Allow any additional properties for LogicAppsUX compatibility */
  [key: string]: unknown;
}

// =============================================================================
// OPERATION MANIFEST
// =============================================================================

/**
 * Complete operation manifest
 */
export interface OperationManifest {
  /** Authoritative display metadata, kept separate from parameter-editor fallbacks. */
  operationMetadata?: {
    source: 'swagger' | 'runtime' | 'discovery';
    name: string | undefined;
    description: string | undefined;
    connectorName: string | undefined;
  };
  properties: OperationManifestProperties;
}

/**
 * Operation manifest properties
 */
export interface OperationManifestProperties {
  // Display
  iconUri: string;
  brandColor: string;
  description?: string;
  summary?: string;

  // Badges
  statusBadge?: Badge;
  environmentBadge?: Badge;

  // Child operations (for control flow)
  allowChildOperations?: boolean;
  childOperationsLocation?: string[];
  subGraphDetails?: Record<string, SubGraphDetail>;

  // Outputs
  outputTokens?: OutputTokens;
  outputs?: SchemaObject;
  isOutputsOptional?: boolean;
  includeRootOutputs?: boolean;

  // Inputs
  inputs?: SchemaObject;
  inputsLocation?: string[];
  isInputsOptional?: boolean;

  // Recurrence (for triggers)
  recurrence?: RecurrenceSetting;

  // Run after
  runAfter?: ManifestRunAfterSetting;

  // Settings
  settings?: OperationManifestSettings;

  // Trigger info
  trigger?: string;
  triggerHint?: string;

  // Connector info
  connector?: ConnectorInfo;

  // Connection
  connection?: ConnectionMetadata;
  connectionReference?: ConnectionReferenceKeyFormatMapping;

  // Documentation
  externalDocs?: Documentation;

  // API info (for service providers)
  api?: Record<string, unknown>;
  visibility?: string;
  operationType?: string;

  /** Allow any additional properties for LogicAppsUX compatibility */
  [key: string]: unknown;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a setting is supported for a given scope
 */
export function isSettingSupported<T>(
  setting: OperationManifestSetting<T> | undefined,
  scope: SettingScope
): boolean {
  if (!setting) return false;
  // If scopes is undefined, setting applies to all scopes
  if (!setting.scopes) return true;
  return setting.scopes.includes(scope);
}

/**
 * Get the scope for an operation (trigger or action)
 */
export function getOperationScope(manifest: OperationManifest): SettingScope {
  return manifest.properties.trigger ? SettingScope.Trigger : SettingScope.Action;
}

/**
 * Check if operation requires a connection
 */
export function requiresConnection(manifest: OperationManifest): boolean {
  return manifest.properties.connection?.required ?? false;
}

/**
 * Get connection type from manifest
 */
export function getConnectionType(manifest: OperationManifest): ConnectionType | undefined {
  return manifest.properties.connection?.type;
}
